import { type PropsWithChildren } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import type { FullPageDialogs } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';

import styles from './FooterNavigation.module.scss';
import { NavigationTabIcon } from './NavigationTabIcon';

interface INavigationItemProps extends PropsWithChildren {
  title?: string;
  onClick: (dialog: FullPageDialogs | 'home') => void;
  dialog: FullPageDialogs | 'home';
  additionalDialogs?: FullPageDialogs[];
  disabled?: boolean;
  comingSoon?: boolean;
  forceActive?: boolean;
  forceInactive?: boolean;
}

export const NavigationTab = observer(function NavigationTab(props: INavigationItemProps) {
  const { uiStore, coinTypeStore } = useStores();
  const { t } = useTranslation();

  const { dialog, additionalDialogs, onClick, children, title, disabled, comingSoon, forceActive, forceInactive } =
    props;

  const normalizedOpenFullPageDialog = uiStore.openFullPageDialog === null ? 'home' : uiStore.openFullPageDialog;
  const dialogs = additionalDialogs ? [dialog, ...additionalDialogs] : [dialog];
  const isActive = forceActive || (!forceInactive && dialogs.includes(normalizedOpenFullPageDialog));
  const isHome = dialog === null || dialog === 'home';
  const lobbyRedesignFlag = useFlag('ui_redesign_lobby', false);

  const handleOnClick = (targetDialog: FullPageDialogs | 'home') => {
    if (lobbyRedesignFlag) {
      void analytics.sendEvent('lobby_navigation', {
        event_type: 'lobby_type',
        event_value: targetDialog as any,
        event_action: 'click',
        event_category: 'lobby_page',
      });
    }
    onClick(targetDialog);
  };

  const onClickHandler = disabled ? undefined : () => handleOnClick(dialog);

  return (
    <div
      className={classNames(styles.navigationItem, {
        [styles.navigationItemActive]: isActive,
        [styles.navigationItemHomeActive]: isHome && isActive,
      })}
      data-testid={`mobile-navitem-${dialog}`}
      onClick={onClickHandler}
    >
      <div
        className={classNames(styles.navigationItemIcon, {
          [styles.navigationItemIconHome]: isHome,
          [styles.navigationItemIconHomeActive]: isHome && isActive,
          [styles.navigationItemIconActive]: isActive,
        })}
      >
        {comingSoon ? (
          <div className={styles.redeemDisabled}>
            <p className={styles.redeemDisabledText}>{t('footer.comingSoon')?.toLowerCase()}</p>
          </div>
        ) : (
          <>
            <NavigationTabIcon
              className={classNames({
                [styles.navigationItemImage]: true,
                [styles.navigationItemImageHide]: !isActive,
                [styles.navigationItemImageActive]: true,
                [styles.navigationItemImageHome]: isHome,
              })}
              coinType={coinTypeStore.coinType}
              isFocused={true}
              route={dialog}
            />
            <NavigationTabIcon
              className={classNames({
                [styles.navigationItemImage]: true,
                [styles.navigationItemImageHide]: isActive,
                [styles.navigationItemImageActive]: false,
                [styles.navigationItemImageHome]: isHome,
              })}
              coinType={coinTypeStore.coinType}
              isFocused={false}
              route={dialog}
            />
          </>
        )}
        {children}
      </div>
      {title ? (
        <span
          className={classNames(styles.navigationItemTitle, {
            [styles.navigationItemTitleActive]: isActive,
            [styles.navigationPrimeItemTitleActive]: isActive && dialog === 'bingo',
          })}
        >
          {title}
        </span>
      ) : null}
    </div>
  );
});
